// ** React Import
import '@styles/react/libs/flatpickr/flatpickr.scss';

import { useReactOidc } from '@axa-fr/react-oidc-context';
import { addMemberCustomerIframe } from '@services/OrganizationService';
import { IntlContext } from '@src/utility/context/Internationalization';
import { dateToString, isObjEmpty, selectThemeColors } from '@utils';
import Loading from '@views/common/Loading';
import Notification from '@views/common/Notification';
import { alertCustom } from '@views/common/NotificationClient';
import { useContext, useEffect, useState } from 'react';
import { Info, User } from 'react-feather';
import { Controller, useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';
import Select from 'react-select';
import { Button, Card, CardBody, Col, Form, FormGroup, Input, Label, Row } from 'reactstrap';

// ** Custom Components
// ** Utils
// ** Context
// ** Third Party Components
// ** Styles
const MemberIframe = () => {
    const { oidcUser, login, logout } = useReactOidc();
    const intlContext = useContext(IntlContext);
    const intl = useIntl();

    useEffect(() => {
        login();
    }, []);

    useEffect(() => {
        if (oidcUser) {
            login();
        } else {
            setTimeout(() => {
                logout()
                login()
            }, 1000)
        }
    }, [oidcUser]);

    const headers = {
        Authorization: `Bearer ${oidcUser && oidcUser.access_token}`,
        'Accept-Language': intlContext.locale,
    };

    const { id, appId, messageId, lang } = useParams();

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const {
        register,
        handleSubmit,
        control,
        formState: { errors },
        unregister,
        reset,
    } = useForm();
    const [identityType, setIdentityType] = useState(null);
    const [admin, setAdmin] = useState(false);
    const [owner, setOwner] = useState(false);
    const [manager, setManager] = useState({
        value: '',
        label: `${intl.formatMessage({ id: 'member' })}`,
    });
    const [registerIdentityNumber, setRegisterIdentityNumber] = useState({
        required: `${intl.formatMessage({ id: 'identityNumberRequired' })}`,
    });

    const identityTypeField = [
        { value: 'idNumber', label: `${intl.formatMessage({ id: 'idNumber' })}` },
        {
            value: 'passportCode',
            label: `${intl.formatMessage({ id: 'passportCode' })}`,
        },
        { value: 'tin', label: `${intl.formatMessage({ id: 'tin' })}` },
        { value: 'ssCode', label: `${intl.formatMessage({ id: 'ssCode' })}` },
    ];
    const roles = [
        { value: '', label: `${intl.formatMessage({ id: 'member' })}` },
        { value: '1', label: `${intl.formatMessage({ id: 'owner' })}` },
        { value: '2', label: `${intl.formatMessage({ id: 'admin' })}` },
    ];

    useEffect(() => {
        intlContext.switchLanguage(lang ?? 'vi');
    }, [intlContext, lang]);

    useEffect(() => {
        if (manager === null || manager?.value === '') {
            setOwner(false);
            setAdmin(false);
        }
        if (manager?.value === '1') {
            setOwner(true);
            setAdmin(false);
        }
        if (manager?.value === '2') {
            setAdmin(true);
            setOwner(false);
        }
    }, [manager]);

    const changeRegisterIdentityNumber = (e) => {
        unregister('identityNumber');
        if (e.value === 'idNumber') {
            setRegisterIdentityNumber({
                required: `${intl.formatMessage({ id: 'identityNumberRequired' })}`,
            });
        } else if (e.value === 'passportCode') {
            setRegisterIdentityNumber({
                required: `${intl.formatMessage({ id: 'identityNumberRequired' })}`,
                pattern: {
                    value: /^[A-Z0-9]{8,12}$/,
                    message: `${intl.formatMessage({ id: 'patternPassportNumber' })}`,
                },
            });
        } else if (e.value === 'tin') {
            setRegisterIdentityNumber({
                required: `${intl.formatMessage({ id: 'identityNumberRequired' })}`,
            });
        } else if (e.value === 'ssCode') {
            setRegisterIdentityNumber({
                required: `${intl.formatMessage({ id: 'identityNumberRequired' })}`,
                pattern: {
                    value: /^[0-9]{10,15}$/,
                    message: `${intl.formatMessage({ id: 'patternSsCode' })}`,
                },
            });
        }
    };

    const onSubmit = (values) => {
        if (isObjEmpty(errors)) {
            setLoading(true);
            const _person = {
                name: values.name,
                enable: true,
                [values.identityType]: values.identityNumber,
                loginInformation: [
                    {
                        email: values.email,
                        phone: values.phoneNumber,
                    },
                ],
                gender: values.gender,
                ethnicity: values.ethnicity,
                // permanentAddress
            };

            if (values?.dob) {
                _person['dob'] = values.dob.length > 0 ? dateToString(values.dob) : null;
            }
            addMemberCustomerIframe(headers, id, appId, messageId, {
                isCreate: true,
                member: _person,
                admin: owner,
                manager: admin,
                // departments: memberDepartments?.map((item) => ({ id: item.value }))
            })
                .then((res) => {
                    setSuccess(
                        `${intl.formatMessage({
                            id: 'member',
                        })} ${intl.formatMessage({ id: 'created' })}`
                    );
                    window.parent.postMessage('success', '*');
                    reset();
                })
                .catch((err) => {
                    if (Object.values(err)[2]?.status === 403) {
                        alertCustom('error', 'Tài khoản của bạn không đủ quyền');
                    } else {
                        setError(err);
                    }
                })
                .finally(() => {
                    setError(null);
                    setSuccess(null);
                    setLoading(false);
                });
        }
    };

    return (
        <Card>
            {loading && <Loading />}
            {error && <Notification data={{ error }} />}
            {success && <Notification data={{ success }} toastId={'success-add'} />}
            <CardBody style={{ height: 380 }}>
                <Form onSubmit={handleSubmit(onSubmit)}>
                    <Row className="">
                        <Col sm="12">
                            <h5 className="mb-1">
                                <User size={20} className="mr-50" />
                                <span className="align-middle">{intl.formatMessage({ id: 'personalInfo' })}</span>
                            </h5>
                        </Col>

                        <Col sm="6">
                            <FormGroup>
                                <Label className="label" for="name">
                                    {intl.formatMessage({ id: 'name' })}
                                </Label>
                                <span className="text-danger">*</span>
                                <Input
                                    type="text"
                                    id="name"
                                    name="name"
                                    placeholder={intl.formatMessage({
                                        id: 'placeholderFullName',
                                    })}
                                    innerRef={register({
                                        required: `${intl.formatMessage({ id: 'nhapHoTen' })}`,
                                        maxLength: {
                                            value: 50,
                                            message: `${intl.formatMessage({
                                                id: 'maxLengthHoTen',
                                            })}`,
                                        },
                                        // pattern: {
                                        //     value: /^[a-zA-Z\sÀÁÂÃÈÉÊÌÍÒÓÔÕÙÚĂĐĨŨƠàáâãèéêìíòóôõùúăđĩũơƯĂẠẢẤẦẨẪẬẮẰẲẴẶẸẺẼỀỀỂưăạảấầẩẫậắằẳẵặẹẻẽềềểỄỆỈỊỌỎỐỒỔỖỘỚỜỞỠỢỤỦỨỪễệỉịọỏốồổỗộớờởỡợụủứừỬỮỰỲỴÝỶỸửữựỳỵỷỹ]*$/,
                                        //     message: `${intl.formatMessage({ id: "patternHoTen" })}`
                                        // }
                                    })}
                                    invalid={errors.name && true}
                                />
                                {errors.name?.message && <p style={{ color: 'red' }}>{errors.name?.message}</p>}
                            </FormGroup>
                        </Col>

                        <Col sm="6">
                            <FormGroup>
                                <Label className="label" for="email">
                                    Email
                                </Label>
                                <span className="text-danger">*</span>
                                <Input
                                    type="email"
                                    id="email"
                                    name="email"
                                    placeholder={intl.formatMessage({ id: 'placeholderEmail' })}
                                    innerRef={register({
                                        required: `${intl.formatMessage({ id: 'nhapEmail' })}`,
                                        pattern: {
                                            value: /\S+@\S+\.\S+/,
                                            message: `Email ${intl.formatMessage({
                                                id: 'match',
                                            })} ${intl.formatMessage({ id: 'format' })}`,
                                        },
                                    })}
                                    invalid={errors.email && true}
                                />
                                {errors.email?.message && <p style={{ color: 'red' }}>{errors.email?.message}</p>}
                            </FormGroup>
                        </Col>

                        <Col sm="12">
                            <h5 className="mb-1">
                                <Info size={15} className="mr-50" />
                                <span className="align-middle">{intl.formatMessage({ id: 'personalCode' })}</span>
                            </h5>
                        </Col>

                        <Col sm="6">
                            <FormGroup>
                                <Label className="label" for="identityType">
                                    {intl.formatMessage({ id: 'identityType' })}
                                </Label>
                                <span className="text-danger">*</span>
                                <Controller
                                    name="identityType"
                                    control={control}
                                    defaultValue={''}
                                    rules={{
                                        required: `${intl.formatMessage({
                                            id: 'identityTypeRequired',
                                        })}`,
                                    }}
                                    render={(props) => {
                                        return (
                                            <Select
                                                {...props}
                                                placeholder={intl.formatMessage({
                                                    id: 'placeholderIdentityType',
                                                })}
                                                theme={selectThemeColors}
                                                options={identityTypeField}
                                                className="react-select$"
                                                classNamePrefix="select"
                                                value={identityType?.value}
                                                isSearchable={true}
                                                isClearable={true}
                                                onChange={(e) => {
                                                    if (e !== undefined && e !== null) {
                                                        props.onChange(e.value);
                                                        setIdentityType(e.value);
                                                        changeRegisterIdentityNumber(e);
                                                    }
                                                }}
                                            />
                                        );
                                    }}
                                    invalid={errors.identityType && true}
                                />
                                {errors.identityType?.message && (
                                    <p style={{ color: 'red' }}>{errors.identityType?.message}</p>
                                )}
                            </FormGroup>
                        </Col>

                        <Col sm="6">
                            <FormGroup>
                                <Label className="label" for="identityNumber">
                                    {intl.formatMessage({ id: 'identityNumber' })}
                                </Label>
                                <span className="text-danger">*</span>
                                <Input
                                    type="text"
                                    id="identityNumber"
                                    name="identityNumber"
                                    placeholder={intl.formatMessage({
                                        id: 'placeholderIdentityNumber',
                                    })}
                                    innerRef={register(registerIdentityNumber)}
                                    invalid={errors.identityNumber && true}
                                />
                                {errors.identityNumber?.message && (
                                    <p style={{ color: 'red' }}>{errors.identityNumber?.message}</p>
                                )}
                            </FormGroup>
                        </Col>
                    </Row>
                    <div className="text-right">
                        <Button type="submit" color="primary">
                            {intl.formatMessage({ id: 'add' })}
                        </Button>
                    </div>
                </Form>
            </CardBody>
        </Card>
    );
};

export default MemberIframe;
